import {FC, useEffect, useState} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import SecurityProfileTab from './tabs/SecurityProfileTab'
import {Helmet} from 'react-helmet'
import {useAuth} from 'app/modules/auth'
import {HeaderDataType} from './components/Header'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  where,
  writeBatch,
} from '@firebase/firestore'
import db from 'services/firebase'
import {FrameworkDataType} from './components/Compliance'
import {PolicyDataType} from './components/Policies'
import {Link, useLocation} from 'react-router-dom'
import RequestsTab, {RequestDataType} from './tabs/RequestsTab'
import {ControlDataType} from './components/Controls'
import {DocumentDataType} from './components/Documents'
import {encryptId} from './utils/helpers'
import Button from 'app/components/Button'
import ShareLinkModal from './components/modals/ShareLinkModal'
import {SubprocessorDataType} from './components/Subprocessors'
import UpdatesTab, {SubscriberDataType, UpdateDataType} from './tabs/UpdatesTab'
import SettingsTab from './tabs/SettingsTab'
import {v4 as uuid} from 'uuid'
import {TrustPageTitle} from '_metronic/layout/core/TrustPageData'
import {FAQDataType} from './components/FAQ'

const tabs: Array<{title: string; href: string; slug: string}> = [
  {
    title: 'Security Profile',
    slug: 'security-profile',
    href: '/trust-center',
  },
  {
    title: 'Requests',
    slug: 'requests',
    href: '/trust-center/requests',
  },
  {
    title: 'Updates',
    slug: 'updates',
    href: '/trust-center/updates',
  },
  {
    title: 'Settings',
    slug: 'settings',
    href: '/trust-center/settings',
  },
]

const TrustWrapper: FC = () => {
  const {currentUser} = useAuth()

  const location = useLocation()

  const [headerDetails, setHeaderDetails] = useState<HeaderDataType>({
    title: '',
    description: '',
    masterServiceAgreement: '',
    privacyPolicy: '',
    links: [],
  })

  const [frameworks, setFrameworks] = useState<FrameworkDataType[]>([])
  const [policies, setPolicies] = useState<PolicyDataType[]>([])
  const [documents, setDocuments] = useState<DocumentDataType[]>([])
  const [controls, setControls] = useState<ControlDataType[]>([])
  const [subprocessors, setSubprocessors] = useState<SubprocessorDataType[]>([])
  const [FAQs, setFAQs] = useState<FAQDataType[]>([])
  const [requests, setRequests] = useState<RequestDataType[]>([])
  const [resources, setResources] = useState<{label: string; value: string}[]>([])
  const [numOfRequests, setNumOfRequests] = useState(0)
  const [updates, setUpdates] = useState<UpdateDataType[]>([])
  const [subscribers, setSubscribers] = useState<SubscriberDataType[]>([])
  const [trustCenterURL, setTrustCenterURL] = useState<string>('')
  const [isShareLinkModalShown, setIsShareLinkModalShown] = useState(false)

  const [activeTab, setActiveTab] = useState(
    location.pathname.split('/').at(-1) === '' ? tabs[0].slug : location.pathname.split('/').at(-1)
  )
  const [isLoading, setIsLoading] = useState(true)

  const [companyDetails, setCompanyDetails] = useState({name: '', logoURL: ''})

  const generateUniqueId = () => {
    return uuid()
  }

  useEffect(() => {
    const tenantId = currentUser?.tenantId || ''
    const companyDetailsRef = doc(db, 'tenants', tenantId, 'settings', 'company')

    const unsubscribe = onSnapshot(companyDetailsRef, (snapshot) => {
      if (snapshot.exists()) {
        setCompanyDetails({
          name: snapshot.data().displayName,
          logoURL: snapshot.data().logoUrl,
        })
      }
    })

    return () => unsubscribe()
  }, [])

  const getHeaderDetails = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const detailsRef = doc(db, 'tenants', tenantId, 'trust_center', 'headerDetails')
      const companyRef = doc(db, 'tenants', tenantId, 'settings', 'company')

      const companyDetails = await getDoc(companyRef)

      let companyName = ''
      let companyLogoURL = ''
      if (companyDetails.exists()) {
        companyName = companyDetails.data().displayName
        companyLogoURL = companyDetails.data().logoUrl
      }

      const unsubscribe = onSnapshot(detailsRef, async (docSnap) => {
        if (docSnap.exists() && docSnap.data().title) {
          setHeaderDetails({
            title: docSnap.data().title,
            description: docSnap.data().description,
            links: docSnap.data().links,
            masterServiceAgreement: docSnap.data().masterServiceAgreement,
            privacyPolicy: docSnap.data().privacyPolicy,
          })
        } else {
          const details = {
            title: `${companyName.trim().length > 0 ? `${companyName}'s ` : ''}Trust Center`,
            description: `At SecureSlate, trust is the foundation of everything we do. Our Trust Center is a centralized hub designed to provide transparency into our security, privacy, compliance, and reliability practices. Here, you’ll find up-to-date information about our certifications, policies, system status, and how we safeguard your data.`,
            masterServiceAgreement: '',
            privacyPolicy: '',
            links: [],
          }

          setHeaderDetails(details)

          await setDoc(detailsRef, {
            ...details,
            companyName: companyName,
            companyLogoURL: companyLogoURL,
          })
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting header details for trust center:', error)
    }
  }

  const getAddedFrameworks = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const frameworksRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'frameworks',
        'all_frameworks'
      )

      const queryRef = query(frameworksRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const frameworks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as FrameworkDataType[]
        setFrameworks(frameworks)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting frameworks details for trust center:', error)
    }
  }

  const getAddedPolicies = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const policiesRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'policies',
        'all_policies'
      )

      const queryRef = query(policiesRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const policies = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as PolicyDataType[]

        setPolicies(policies)

        setResources((prev) => {
          return [
            ...prev,
            ...policies
              .filter((policy) => policy.access.toLowerCase() === 'restricted')
              .map((policy) => ({
                label: policy.title,
                value: policy.id,
              })),
          ]
        })
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting policies details for trust center:', error)
    }
  }

  const getAddedDocuments = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const documentsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'documents',
        'all_documents'
      )

      const queryRef = query(documentsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const documents = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as DocumentDataType[]

        setDocuments(documents)

        setResources((prev) => {
          return [
            ...prev,
            ...documents
              .filter((document) => document.access.toLowerCase() === 'restricted')
              .map((document) => ({
                label: document.title,
                value: document.id,
              })),
          ]
        })
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting documents details for trust center:', error)
    }
  }

  const getRequests = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const allRequestsRef = collection(db, 'tenants', tenantId, 'requests_access')

      const queryRef = query(allRequestsRef, orderBy('requestedDate', 'desc'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const requests = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as RequestDataType[]

        setRequests(requests)
        setNumOfRequests(requests.filter((request) => request.status === 'Needs Decision').length)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting resources requests for trust center:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getControls = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const controlsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'controls',
        'all_controls'
      )

      const queryRef = query(controlsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const controls = snapshot.docs
          .filter((doc) => doc.data()?.isShownInTrustCenter)
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as ControlDataType[]
        setControls(controls)
      })

      return unsubscribe
    } catch (error) {
      console.error('Errow while getting controls for trust center', error)
    }
  }

  const fillDefaultControlsInTrustCenter = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const allControlsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'controls',
        'all_controls'
      )
      const queryRef = query(allControlsRef, limit(1))
      const snapshot = await getDocs(queryRef)

      if (snapshot.empty) {
        const batch = writeBatch(db)
        const controlsSnapshot = await getDocs(collection(db, 'tenants', tenantId, 'controls'))
        controlsSnapshot.forEach((controlDoc) => {
          if (
            controlDoc.data().control_framework_mappings &&
            !controlDoc.data().control_framework_mappings.startsWith('ISO 27001:2013')
          ) {
            batch.set(doc(allControlsRef, controlDoc.id), {
              id: controlDoc.id,
              title: controlDoc.data().control_title,
              description: controlDoc.data().control_description,
              category: controlDoc.data()?.control_category || 'Custom',
              isShownInTrustCenter: false,
            })
          }
        })

        await batch.commit()
      }
    } catch (error) {
      console.log('Error while populating default controls in trust center')
    }
  }

  const getAddedSubprocessors = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const documentsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'subprocessors',
        'all_subprocessors'
      )

      const queryRef = query(documentsRef, orderBy('title'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const subprocessors = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SubprocessorDataType[]

        setSubprocessors(subprocessors)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting subprocessors for trust center:', error)
    }
  }

  const getAddedUpdates = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const documentsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'updates',
        'all_updates'
      )

      const queryRef = query(documentsRef, orderBy('createdAt', 'desc'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const updates = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as UpdateDataType[]

        setUpdates(updates)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting updates data for trust center:', error)
    }
  }

  const getSubscriberList = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const documentsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'subscribers',
        'all_subscribers'
      )

      const queryRef = query(documentsRef, orderBy('subscribedOn', 'desc'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const subscribers = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as SubscriberDataType[]

        setSubscribers(subscribers)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting subscribers list for trust center:', error)
    }
  }

  const getTrustCenterURL = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const settingsRef = doc(db, 'tenants', tenantId, 'trust_center', 'settings')
      const domainsRef = collection(db, 'trust_center_domains')
      const companyRef = doc(db, 'tenants', tenantId, 'settings', 'company')

      const companyDetails = await getDoc(companyRef)

      let companyName = ''
      if (companyDetails.exists()) {
        companyName = companyDetails.data().displayName
      }

      const unsubscribe = onSnapshot(settingsRef, async (docSnap) => {
        if (docSnap.exists() && docSnap.data().trustCenterURL) {
          setTrustCenterURL(docSnap.data().trustCenterURL)
        } else {
          let url = `${window.location.origin}/trust`

          if (companyName.trim().length > 0) {
            url = `${url}/${companyName.trim().split(/\s+/).join('-')}`
          } else {
            url = `${url}/${generateUniqueId()}`
          }

          const queryRef = query(domainsRef, where('trustCenterURL', '==', url))

          const querySnapshot = await getDocs(queryRef)

          if (!querySnapshot.empty) {
            url = `${window.location.origin}/trust/${generateUniqueId()}`
          }

          setTrustCenterURL(url)

          await setDoc(settingsRef, {
            trustCenterURL: url,
          })

          await addDoc(domainsRef, {
            encryptedTenantId: encryptId(tenantId),
            trustCenterURL: url,
          })
        }
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting trust center url', error)
    }
  }

  const getAddedFAQs = async () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const faqsFef = collection(db, 'tenants', tenantId, 'trust_center', 'faqs', 'all_faqs')

      const queryRef = query(faqsFef, orderBy('updatedAt', 'desc'))

      const unsubscribe = onSnapshot(queryRef, (snapshot) => {
        const faqs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as FAQDataType[]
        setFAQs(faqs)
      })

      return unsubscribe
    } catch (error) {
      console.error('Error while getting faqs for trust center:', error)
    }
  }

  useEffect(() => {
    fillDefaultControlsInTrustCenter()
    getHeaderDetails()
    getAddedFrameworks()
    getAddedPolicies()
    getAddedDocuments()
    getAddedSubprocessors()
    getAddedFAQs()
    getControls()
    getAddedUpdates()
    getSubscriberList()
    getTrustCenterURL()
    getRequests()
  }, [])

  const renderTab = () => {
    switch (activeTab) {
      case 'security-profile':
        return (
          <SecurityProfileTab
            headerDetails={headerDetails}
            frameworks={frameworks}
            policies={policies}
            documents={documents}
            controls={controls}
            subprocessors={subprocessors}
            FAQs={FAQs}
          />
        )
      case 'requests':
        return <RequestsTab requests={requests} resources={resources} />
      case 'updates':
        return <UpdatesTab addedUpdates={updates} subscriberList={subscribers} />
      case 'settings':
        return <SettingsTab trustCenterURL={trustCenterURL} />
    }
  }

  useEffect(() => {
    let isFirstTab = true

    for (const tab of tabs) {
      if (location.pathname.includes(tab.slug)) {
        setActiveTab(tab.slug)
        isFirstTab = false
        break
      }
    }

    if (isFirstTab) setActiveTab(tabs[0].slug)
  }, [location.pathname])

  return (
    <>
      {/* <TrustPageTitle
        icon='Icon-3.svg'
        title='Trust Center'
        desc='Explore and manage your security and compliance.'
      >
        {headerDetails.title}
      </TrustPageTitle> */}
      <Helmet>
        <title>SecureSlate | Trust Center</title>
      </Helmet>

      <div className='min-vh-100 d-flex flex-column pt-4 pt-lg-0'>
        {isLoading || headerDetails.title.length === 0 ? (
          <div
            style={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {(!companyDetails.logoURL ||
              companyDetails.logoURL === '/media/svg/avatars/blank.svg') && (
              <div
                className='d-flex align-items-center'
                style={{
                  backgroundColor: '#F3FAF7',
                  border: '1px solid #0E9F6E',
                  borderRadius: '8px',
                  gap: '20px',
                  padding: '16px 20px',
                  marginBottom: '40px',
                }}
              >
                <div className='flex-fill d-flex' style={{gap: '12px'}}>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#BCF0DA',
                      borderRadius: '12px',
                      height: '40px',
                      width: '40px',
                    }}
                  >
                    <img
                      src={toAbsoluteUrl('/media/icon/building-community.svg')}
                      alt='building-community-icon'
                    />
                  </div>

                  <div className=''>
                    <h4
                      className='text-dark'
                      style={{fontSize: '14px', fontWeight: '600', marginBottom: '4px'}}
                    >
                      Company&nbsp;{!companyDetails.name && `Name `}
                      {(!companyDetails.logoURL ||
                        companyDetails.logoURL === '/media/svg/avatars/blank.svg') &&
                        !companyDetails.name &&
                        `and `}
                      {(!companyDetails.logoURL ||
                        companyDetails.logoURL === '/media/svg/avatars/blank.svg') &&
                        `Logo `}
                      Missing!
                    </h4>

                    <p className='text-dark mb-0' style={{fontSize: '13px'}}>
                      You should update your company's&nbsp;{!companyDetails.name && `name `}
                      {(!companyDetails.logoURL ||
                        companyDetails.logoURL === '/media/svg/avatars/blank.svg') &&
                        !companyDetails.name &&
                        `and `}
                      {(!companyDetails.logoURL ||
                        companyDetails.logoURL === '/media/svg/avatars/blank.svg') &&
                        `logo `}
                      in order to publish your Trust Center.
                    </p>
                  </div>
                </div>

                <Link to='/company-settings/company_info'>
                  <Button btnType='primary'>Update</Button>
                </Link>
              </div>
            )}

            {/* header goes here  */}
            {/* <div className='d-flex align-items-center mb-10'>
              <h3 className='text-dark fs-1 mb-0' style={{fontWeight: '600'}}>
                {headerDetails.title}
              </h3>

              <div className='ms-auto d-flex' style={{gap: '12px'}}>
                <Button onClick={() => setIsShareLinkModalShown(true)}>
                  <img
                    alt={'share-icon'}
                    src={toAbsoluteUrl('/media/icon/share.svg')}
                    className='me-2'
                    style={{height: '18px', width: '18px'}}
                  />
                  <span>Share</span>
                </Button>

                <a href={`${trustCenterURL}`} target='_blank'>
                  <Button>
                    <img
                      alt={'eye-icon'}
                      src={toAbsoluteUrl('/media/icon/eye.svg')}
                      className='me-2'
                      style={{height: '18px', width: '18px'}}
                    />
                    <span>Preview</span>
                  </Button>
                </a>
              </div>
            </div> */}

            {/* toolbar goes here  */}
            {/* <div className='d-flex h-55px mb-8 border-bottom' >
              <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-semibold flex-nowrap '>
                {tabs.map((tab) => (
                  <li key={tab.slug} className='nav-item'>
                    <Link
                      to={tab.href}
                      className={`nav-link me-6 ${
                        activeTab === tab.slug ? 'text-active-primary active' : ''
                      }`}
                    >
                      {tab.title}

                      {tab.title.toLowerCase() === 'requests' && (
                        <div
                          className='d-flex justify-content-center align-items-center'
                          style={{
                            background: '#E5E7EB',
                            borderRadius: '9999px',
                            width: '20px',
                            height: '20px',
                            marginInlineStart: '6px',
                          }}
                        >
                          <span className='fw-medium' style={{color: '#1F2A37', fontSize: '12px'}}>
                            {numOfRequests}
                          </span>
                        </div>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div> */}

            {/* <div className='' style={{borderBottom: '1px solid #D1D5DB', marginBottom: '32px'}}>
              <div className='d-flex flex-nowrap' style={{gap: '16px'}}>
                {tabs.map((tab, index) => (
                  <Link
                    key={tab.slug}
                    to={tab.href}
                    className='d-flex align-items-center'
                    style={{
                      borderBottom: `${
                        activeTab === tab.slug ? '2px solid #0e9f6e' : '2px solid transparent'
                      }`,
                      paddingBottom: '16px',
                    }}
                  >
                    <span
                      className={`lh-sm fw-medium ${
                        activeTab === tab.slug ? 'text-gray-900' : 'text-gray-500'
                      }`}
                      style={{
                        fontSize: '14px',
                      }}
                    >
                      {tab.title}
                    </span>

                    {tab.title.toLowerCase() === 'requests' && (
                      <div
                        className='d-flex justify-content-center align-items-center'
                        style={{
                          background: '#E5E7EB',
                          borderRadius: '9999px',
                          width: '20px',
                          height: '20px',
                          marginInlineStart: '6px',
                        }}
                      >
                        <span className='fw-medium' style={{color: '#1F2A37', fontSize: '12px'}}>
                          {numOfRequests}
                        </span>
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div> */}

            {/* tab goes here   */}
            {/* <div className=''>{renderTab()}</div> */}

            {/* <div className={`card mb-5 mb-xl-8`}>
              <div className='card-header border-0 pt-5 mb-5'>
                <div className='d-flex align-items-center'>
                  <img src={toAbsoluteUrl(`/media/logos/Icon-3.svg`)} alt='' />
                  <div
                    className='d-flex align-items-center justify-content-center border border-gray-300 rounded-circle  me-5'
                    style={{width: '45px', height: '45px'}}
                  >
                    <KTIcon iconName={'abstract-41'} className='fs-2 text-primary' />
                  </div>

                  <div className='d-flex justify-content-start flex-column'>
                    <span className='card-label fw-bold fs-5'>Trust Center</span>
                    <span className='fw-semibold fs-6' style={{color: '#5A5A5A'}}>
                      Explore and manage your security and compliance.
                    </span>
                  </div>
                </div>

                <div className='ms-auto d-flex' style={{gap: '12px'}}>
                  <Button onClick={() => setIsShareLinkModalShown(true)}>
                    <img
                      alt={'share-icon'}
                      src={toAbsoluteUrl('/media/icon/share.svg')}
                      className='me-2'
                      style={{height: '18px', width: '18px'}}
                    />
                    <span>Share</span>
                  </Button>

                  <a href={`${trustCenterURL}`} target='_blank'>
                    <Button>
                      <img
                        alt={'eye-icon'}
                        src={toAbsoluteUrl('/media/icon/eye.svg')}
                        className='me-2'
                        style={{height: '18px', width: '18px'}}
                      />
                      <span>Preview</span>
                    </Button>
                  </a>
                </div>
              </div>

              <div className='px-10 '>
                <div className='d-flex h-55px border-bottom'>
                  <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-semibold flex-nowrap '>
                    {tabs.map((tab) => (
                      <li key={tab.slug} className='nav-item'>
                        <Link
                          to={tab.href}
                          className={`nav-link me-6 ${
                            activeTab === tab.slug ? 'text-active-primary active' : ''
                          }`}
                        >
                          {tab.title}

                          {tab.title.toLowerCase() === 'requests' && (
                            <div
                              className='d-flex justify-content-center align-items-center'
                              style={{
                                background: '#E5E7EB',
                                borderRadius: '9999px',
                                width: '20px',
                                height: '20px',
                                marginInlineStart: '6px',
                              }}
                            >
                              <span
                                className='fw-medium'
                                style={{color: '#1F2A37', fontSize: '12px'}}
                              >
                                {numOfRequests}
                              </span>
                            </div>
                          )}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div> */}

            <TrustPageTitle
              icon='Icon-7.svg'
              title='Trust Center'
              desc='Explore and manage your security and compliance.'
              tabs={tabs.map((tab) =>
                tab.slug === 'requests' ? {...tab, badgeValue: numOfRequests.toString()} : {...tab}
              )}
              activeTab={activeTab}
              sideButtonGroup={
                <>
                  <Button onClick={() => setIsShareLinkModalShown(true)}>
                    <img
                      alt={'share-icon'}
                      src={toAbsoluteUrl('/media/icon/share.svg')}
                      className='me-2'
                      style={{height: '18px', width: '18px'}}
                    />
                    <span>Share</span>
                  </Button>

                  <a href={`${trustCenterURL}`} target='_blank'>
                    <Button>
                      <img
                        alt={'eye-icon'}
                        src={toAbsoluteUrl('/media/icon/eye.svg')}
                        className='me-2'
                        style={{height: '18px', width: '18px'}}
                      />
                      <span>Preview</span>
                    </Button>
                  </a>
                </>
              }
            >
              {headerDetails.title}
            </TrustPageTitle>

            {/* tab goes here   */}
            <div className=''>{renderTab()}</div>
          </>
        )}
      </div>

      {isShareLinkModalShown && (
        <ShareLinkModal
          isModalOpen={isShareLinkModalShown}
          hideModal={() => setIsShareLinkModalShown(false)}
          url={trustCenterURL}
        />
      )}
    </>
  )
}

export default TrustWrapper
